import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

/**
 * Theme — three modes:
 *   - 'system': follow the OS preference (the default; reacts to changes live)
 *   - 'light' / 'dark': explicit override stored in localStorage
 *
 * Tailwind is in `class` darkMode, so the effective mode is applied by toggling
 * `class="dark"` on <html>. We do that here on init + whenever preference or
 * the system query changes.
 */
type Mode = 'system' | 'light' | 'dark';
const STORAGE_KEY = 'billspilt:theme';

function readStored(): Mode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    /* ignore */
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export const useThemeStore = defineStore('theme', () => {
  const mode = ref<Mode>(readStored());

  const effective = computed<'light' | 'dark'>(() =>
    mode.value === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : mode.value,
  );

  function apply() {
    const isDark = effective.value === 'dark';
    document.documentElement.classList.toggle('dark', isDark);
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
  }

  function setMode(next: Mode) {
    mode.value = next;
    try {
      if (next === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    apply();
  }

  /** Cycle: system → light → dark → system. Bound to the header toggle. */
  function cycle() {
    setMode(mode.value === 'system' ? 'light' : mode.value === 'light' ? 'dark' : 'system');
  }

  function init() {
    apply();
    // Live-react when the user is on 'system' and flips their OS preference.
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      const onChange = () => {
        if (mode.value === 'system') apply();
      };
      try {
        mql.addEventListener('change', onChange);
      } catch {
        // Safari < 14
        mql.addListener(onChange);
      }
    }
    watch(mode, apply);
  }

  return { mode, effective, setMode, cycle, init };
});